import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Save, Plus, X, RotateCcw, Loader2, Mail, Trash2, ExternalLink } from 'lucide-react';

export default function AboutEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [tab, setTab] = useState('content');
  const [inquiries, setInquiries] = useState([]);
  const [inqStatus, setInqStatus] = useState('all');

  useEffect(() => {
    api.get('/about').then(r => setData(r.data));
    refreshInquiries();
  }, []);

  const refreshInquiries = () => api.get('/admin/inquiries').then(r => setInquiries(r.data || []));

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await api.put('/admin/about', data);
      setMsg('Saved.');
      setTimeout(() => setMsg(''), 2500);
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset the entire About page to default content?')) return;
    await api.post('/admin/about/reset');
    const r = await api.get('/about'); setData(r.data);
    setMsg('Reset to defaults.');
    setTimeout(() => setMsg(''), 2500);
  };

  const updateInquiry = async (id, patch) => { await api.patch(`/admin/inquiries/${id}`, patch); refreshInquiries(); };
  const deleteInquiry = async (id) => { if (!window.confirm('Delete this inquiry?')) return; await api.delete(`/admin/inquiries/${id}`); refreshInquiries(); };

  if (!data) return <div className="py-12 text-center text-muted-kc">Loading…</div>;

  const filteredInq = inqStatus === 'all' ? inquiries : inquiries.filter(i => (i.status || 'new') === inqStatus);

  return (
    <div data-testid="about-editor">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">About Page</div>
          <div className="font-display uppercase text-2xl mt-1">Edit Mickey’s about content</div>
        </div>
        <div className="flex gap-2">
          <a href="/about" target="_blank" rel="noopener noreferrer" className="h-10 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] border border-kc-strong hover:bg-surface-2 text-sm font-semibold"><ExternalLink size={14}/> Preview</a>
          <button onClick={reset} className="h-10 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] border border-kc-strong hover:bg-surface-2 text-sm font-semibold" data-testid="about-reset"><RotateCcw size={14}/> Reset</button>
          <button onClick={save} disabled={saving} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black text-sm font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-50" data-testid="about-save">{saving ? <><Loader2 size={14} className="animate-spin"/> Saving</> : <><Save size={14}/> Save</>}</button>
        </div>
      </div>
      {msg && <div className="mb-4 p-3 bg-gold-soft border border-[color:var(--kc-gold)] text-ink rounded-[var(--kc-radius-sm)] text-sm font-semibold">{msg}</div>}

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-5 border-b border-kc">
        {[{id:'content',label:'Content'},{id:'inquiries',label:`Inquiries (${inquiries.length})`}].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === t.id ? 'border-[color:var(--kc-gold)] text-ink' : 'border-transparent text-muted-kc hover:text-ink'}`} data-testid={`about-subtab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'content' && (
        <div className="space-y-6">
          <Group title="Hero">
            <Field label="Eyebrow" v={data.eyebrow} on={(v) => set('eyebrow', v)} testid="a-eyebrow"/>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Title (top line)" v={data.title_top} on={(v) => set('title_top', v)} testid="a-title-top"/>
              <Field label="Title (bottom — gold)" v={data.title_bottom} on={(v) => set('title_bottom', v)} testid="a-title-bot"/>
            </div>
            <Field label="Subtitle" v={data.subtitle} on={(v) => set('subtitle', v)} multi testid="a-subtitle"/>
            <Field label="Portrait image URL" v={data.portrait_image} on={(v) => set('portrait_image', v)} testid="a-portrait"/>
          </Group>

          <Group title="Highlights (the 4 chips)">
            {(data.highlights || []).map((h, i) => (
              <div key={i} className="bg-surface-2 p-3 rounded-[var(--kc-radius-sm)] grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
                <input value={h.title} onChange={(e) => { const x = [...data.highlights]; x[i] = {...x[i], title: e.target.value.toUpperCase()}; set('highlights', x); }} placeholder="TITLE" className="kc-input md:col-span-3 uppercase font-semibold" data-testid={`a-hl-t-${i}`}/>
                <input value={h.body} onChange={(e) => { const x = [...data.highlights]; x[i] = {...x[i], body: e.target.value}; set('highlights', x); }} placeholder="Body" className="kc-input md:col-span-8" data-testid={`a-hl-b-${i}`}/>
                <button onClick={() => set('highlights', data.highlights.filter((_, idx) => idx !== i))} className="text-[color:var(--kc-danger)] p-2 md:col-span-1"><X size={14}/></button>
              </div>
            ))}
            <button onClick={() => set('highlights', [...(data.highlights||[]), {title:'NEW',body:'Description'}])} className="text-sm font-semibold inline-flex items-center gap-1 text-gold"><Plus size={14}/> Add highlight</button>
          </Group>

          <Group title="Story (paragraphs)">
            {(data.story || []).map((p, i) => (
              <div key={i} className="bg-surface-2 p-3 rounded-[var(--kc-radius-sm)] relative">
                <button onClick={() => set('story', data.story.filter((_, idx) => idx !== i))} className="absolute top-2 right-2 text-[color:var(--kc-danger)]"><X size={14}/></button>
                <textarea value={p} onChange={(e) => { const x = [...data.story]; x[i] = e.target.value; set('story', x); }} rows={3} className="kc-input pr-8" data-testid={`a-story-${i}`}/>
              </div>
            ))}
            <button onClick={() => set('story', [...(data.story||[]), 'New paragraph...'])} className="text-sm font-semibold inline-flex items-center gap-1 text-gold"><Plus size={14}/> Add paragraph</button>
            <Field label="Motto" v={data.motto} on={(v) => set('motto', v)} testid="a-motto"/>
          </Group>

          <Group title="Stats (animated)">
            {(data.stats || []).map((s, i) => (
              <div key={i} className="bg-surface-2 p-3 rounded-[var(--kc-radius-sm)] grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
                <input value={s.value || ''} onChange={(e) => { const x = [...data.stats]; x[i] = {...x[i], value: e.target.value}; set('stats', x); }} placeholder="Value" className="kc-input md:col-span-3 font-display text-lg" data-testid={`a-stat-v-${i}`}/>
                <input value={s.suffix || ''} onChange={(e) => { const x = [...data.stats]; x[i] = {...x[i], suffix: e.target.value}; set('stats', x); }} placeholder="+" className="kc-input md:col-span-2" data-testid={`a-stat-sfx-${i}`}/>
                <input value={s.label || ''} onChange={(e) => { const x = [...data.stats]; x[i] = {...x[i], label: e.target.value}; set('stats', x); }} placeholder="Label" className="kc-input md:col-span-6" data-testid={`a-stat-l-${i}`}/>
                <button onClick={() => set('stats', data.stats.filter((_, idx) => idx !== i))} className="text-[color:var(--kc-danger)] p-2 md:col-span-1"><X size={14}/></button>
              </div>
            ))}
            <button onClick={() => set('stats', [...(data.stats||[]), {value:'0',suffix:'',label:'New stat'}])} className="text-sm font-semibold inline-flex items-center gap-1 text-gold"><Plus size={14}/> Add stat</button>
          </Group>

          <Group title="Social Links">
            <Field label="Eyebrow" v={data.socials_eyebrow} on={(v) => set('socials_eyebrow', v)} testid="a-soc-eb"/>
            <Field label="Body" v={data.socials_body} on={(v) => set('socials_body', v)} multi testid="a-soc-body"/>
            {(data.socials || []).map((s, i) => (
              <div key={i} className="bg-surface-2 p-3 rounded-[var(--kc-radius-sm)] grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
                <input value={s.label || ''} onChange={(e) => { const x = [...data.socials]; x[i] = {...x[i], label: e.target.value}; set('socials', x); }} placeholder="Instagram" className="kc-input md:col-span-3" data-testid={`a-soc-l-${i}`}/>
                <input value={s.handle || ''} onChange={(e) => { const x = [...data.socials]; x[i] = {...x[i], handle: e.target.value}; set('socials', x); }} placeholder="@MickeyTrivett" className="kc-input md:col-span-3" data-testid={`a-soc-h-${i}`}/>
                <input value={s.url || ''} onChange={(e) => { const x = [...data.socials]; x[i] = {...x[i], url: e.target.value}; set('socials', x); }} placeholder="https://..." className="kc-input md:col-span-4" data-testid={`a-soc-u-${i}`}/>
                <select value={s.icon || 'instagram'} onChange={(e) => { const x = [...data.socials]; x[i] = {...x[i], icon: e.target.value}; set('socials', x); }} className="kc-input md:col-span-1">
                  {['instagram','tiktok','facebook','x','youtube','linkedin','website'].map(o => <option key={o}>{o}</option>)}
                </select>
                <button onClick={() => set('socials', data.socials.filter((_, idx) => idx !== i))} className="text-[color:var(--kc-danger)] p-2 md:col-span-1"><X size={14}/></button>
              </div>
            ))}
            <button onClick={() => set('socials', [...(data.socials||[]), {label:'New',handle:'@MickeyTrivett',url:'',icon:'instagram'}])} className="text-sm font-semibold inline-flex items-center gap-1 text-gold"><Plus size={14}/> Add social link</button>
          </Group>

          <Group title="Book Me / Request Info Form">
            <Field label="Eyebrow" v={data.book_me_eyebrow} on={(v) => set('book_me_eyebrow', v)} testid="a-bm-eb"/>
            <Field label="Title" v={data.book_me_title} on={(v) => set('book_me_title', v)} testid="a-bm-title"/>
            <Field label="Body" v={data.book_me_body} on={(v) => set('book_me_body', v)} multi testid="a-bm-body"/>
            <Field label="Inquiry types (comma-separated)" v={(data.inquiry_types || []).join(', ')} on={(v) => set('inquiry_types', v.split(',').map(x => x.trim()).filter(Boolean))} testid="a-bm-types"/>
          </Group>
        </div>
      )}

      {tab === 'inquiries' && (
        <div>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {['all','new','reviewed','replied','closed'].map(s => (
              <button key={s} onClick={() => setInqStatus(s)} className={`px-3 h-8 text-xs font-semibold uppercase tracking-wider rounded-full ${inqStatus === s ? 'bg-ink text-white' : 'bg-surface-2 text-ink-2'}`} data-testid={`inq-filter-${s}`}>{s} ({s === 'all' ? inquiries.length : inquiries.filter(i => (i.status||'new')===s).length})</button>
            ))}
          </div>
          <div className="space-y-3">
            {filteredInq.length === 0 && <div className="p-8 text-center text-muted-kc bg-surface-2 rounded-[var(--kc-radius-md)]">No inquiries yet. Once the form is used on the public About page, submissions appear here.</div>}
            {filteredInq.map((q) => (
              <div key={q.id} className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4" data-testid={`inq-${q.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-ink text-white inline-flex items-center justify-center font-bold">{(q.name||'?')[0]}</div>
                    <div>
                      <div className="font-semibold text-sm">{q.name} <span className="text-muted-kc font-normal">· {q.email}</span></div>
                      <div className="text-[10px] tracking-wider uppercase text-muted-kc">{q.inquiry_type} · {new Date(q.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={q.status || 'new'} onChange={(e) => updateInquiry(q.id, {status: e.target.value})} className="kc-input-sm" data-testid="inq-status-set">
                      {['new','reviewed','replied','closed'].map(s => <option key={s}>{s}</option>)}
                    </select>
                    <a href={`mailto:${q.email}?subject=Re: Your inquiry to Mickey Trivett`} className="h-8 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black text-xs font-semibold" data-testid="inq-reply"><Mail size={12}/> Reply</a>
                    <button onClick={() => deleteInquiry(q.id)} className="text-[color:var(--kc-danger)] p-1" data-testid="inq-del"><Trash2 size={14}/></button>
                  </div>
                </div>
                {q.organization && <div className="text-xs text-muted-kc mt-1">{q.organization}{q.phone ? ` · ${q.phone}` : ''}</div>}
                <p className="mt-3 text-sm text-ink-2 leading-relaxed whitespace-pre-line">{q.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        .kc-input { width: 100%; padding: 0.55rem 0.75rem; border: 1px solid var(--kc-border-strong); border-radius: var(--kc-radius-sm); font-size: 0.9rem; outline: none; background: white; }
        .kc-input:focus { border-color: var(--kc-gold); box-shadow: 0 0 0 2px var(--kc-gold-soft); }
        .kc-input-sm { padding: 0.35rem 0.5rem; border: 1px solid var(--kc-border-strong); border-radius: var(--kc-radius-sm); font-size: 0.8rem; outline: none; background: white; }
      `}</style>
    </div>
  );
}

const Group = ({ title, children }) => (
  <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-5 space-y-3">
    <div className="font-display uppercase text-base text-gold tracking-wider">{title}</div>
    {children}
  </div>
);
const Field = ({ label, v, on, testid, multi }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider mb-1">{label}</label>
    {multi ? <textarea value={v || ''} onChange={(e) => on(e.target.value)} rows={3} className="kc-input" data-testid={testid}/> : <input value={v || ''} onChange={(e) => on(e.target.value)} className="kc-input" data-testid={testid}/>}
  </div>
);
